import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Switch } from 'antd';
import { LeftCircleOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiGetIrisProviders, apiUpdateIrisProvider } from '../../api';
import { useLoading } from '../../context/LoadingContext';
import { toastError, toastSuccess } from '../../utils/toast';
import { IrisProviderFormValues, buildUpdateIrisProviderPayload } from '../../interfaces/iris-provider-form.interface';

const INPUT_STYLE = {
  width: '90%',
};

const ICON_STYLE = {
  marginLeft: 8,
  fontSize: '16px',
  color: '#999',
};

const rules = {
  name: [
    { required: true, message: 'Vui lòng nhập tên nhà cung cấp' },
  ],
  userId: [
    { required: true, message: 'Vui lòng nhập User ID' },
  ],
  username: [
    { required: true, message: 'Vui lòng nhập username' },
  ],
  apiUrl: [
    { required: true, message: 'Vui lòng nhập API URL' },
    { type: 'url' as const, message: 'Vui lòng nhập URL hợp lệ' },
  ],
  otpConfirm: [
    { required: true, message: 'Vui lòng nhập mã OTP' },
    { min: 6, message: 'Mã OTP phải có ít nhất 6 ký tự' },
  ],
};

export default function EditIrisProvider() {
  const [form] = Form.useForm<IrisProviderFormValues>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsLoading } = useLoading();
  const [submitting, setSubmitting] = useState(false);
  const [providerName, setProviderName] = useState<string>('');

  const fetchProviderDetail = useCallback(async (name: string) => {
    try {
      setIsLoading(true);
      const response = await apiGetIrisProviders();
      if (response.status === 200 && response.data.irisProviders) {
        const provider = response.data.irisProviders.find((p: any) => p.name === name);
        if (provider) {
          form.setFieldsValue({
            name: provider.name,
            userId: provider.userId,
            username: provider.username,
            password: '',
            apiUrl: provider.apiUrl,
            rsaPrivateKey: '',
            isActive: provider.isActive ?? provider.status !== 'Inactive',
            proxyAddress: provider.proxyAddress && provider.proxyAddress.length > 0 ? provider.proxyAddress : [''],
          });
        } else {
          toastError('Không tìm thấy nhà cung cấp');
          navigate(-1);
        }
      }
    } catch (error: any) {
      console.error('Error fetching IRIS provider detail:', error);
      toastError('Không thể tải thông tin nhà cung cấp');
    } finally {
      setIsLoading(false);
    }
  }, [form, setIsLoading, navigate]);

  useEffect(() => {
    const name = location.state?.name || new URLSearchParams(location.search).get('name');
    if (name) {
      setProviderName(name);
      fetchProviderDetail(name);
    } else {
      toastError('Không tìm thấy tên nhà cung cấp');
      navigate(-1);
    }
  }, [location, fetchProviderDetail, navigate]);

  const handleSubmit = async (values: IrisProviderFormValues) => {
    try {
      setSubmitting(true);
      setIsLoading(true);

      const payload = buildUpdateIrisProviderPayload(providerName, values);
      const resp = await apiUpdateIrisProvider(payload);
      if (resp.status === 200) {
        toastSuccess('Cập nhật dữ liệu thành công!');
        navigate(-1);
      }
    } catch (error: any) {
      console.error('Error updating IRIS provider:', error);
      toastError('Vui lòng kiểm tra lại thông tin!');
    } finally {
      setSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 200, marginBottom: 20 }}>
        <Button
          onClick={() => navigate(-1)}
          icon={<LeftCircleOutlined />}
          style={{ background: 'gray', color: 'white' }}
        >
          Trở lại
        </Button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Form
          style={{ width: '700px' }}
          form={form}
          onFinish={handleSubmit}
          labelAlign="left"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValues={{
            isActive: true,
            proxyAddress: [''],
          }}
        >
          <Form.Item
            label="Tên nhà cung cấp"
            name="name"
            rules={rules.name}
          >
            <Input placeholder="Nhập tên nhà cung cấp" style={INPUT_STYLE} disabled />
          </Form.Item>

          <Form.Item
            label="User ID"
            name="userId"
            rules={rules.userId}
          >
            <Input placeholder="Nhập User ID" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Username"
            name="username"
            rules={rules.username}
          >
            <Input placeholder="Nhập username" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
          >
            <Input.Password placeholder="Để trống nếu không đổi" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="API URL"
            name="apiUrl"
            rules={rules.apiUrl}
          >
            <Input placeholder="https://example.com/api" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item
            label="RSA private key"
            name="rsaPrivateKey"
          >
            <Input.TextArea placeholder="Để trống nếu không đổi" style={INPUT_STYLE} autoSize={{ minRows: 4, maxRows: 8 }} />
          </Form.Item>

          <Form.Item
            label="Trạng thái"
            name="isActive"
            valuePropName="checked"
          >
            <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
          </Form.Item>

          <Form.List name="proxyAddress">
            {(fields, { add, remove }) => (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {fields.map((field, index) => (
                  <Form.Item
                    label={index === 0 ? 'Proxy Address' : ' '}
                    colon={index === 0}
                    key={field.key}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Form.Item
                        {...field}
                        validateTrigger={['onChange', 'onBlur']}
                        noStyle
                      >
                        <Input
                          placeholder="Nhập địa chỉ proxy"
                          style={INPUT_STYLE}
                        />
                      </Form.Item>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        {fields.length > 1 && (
                          <MinusCircleOutlined
                            className="dynamic-delete-button"
                            onClick={() => remove(field.name)}
                            style={ICON_STYLE}
                          />
                        )}
                        {index === fields.length - 1 && (
                          <PlusOutlined
                            onClick={() => add()}
                            style={ICON_STYLE}
                          />
                        )}
                      </div>
                    </div>
                  </Form.Item>
                ))}
              </div>
            )}
          </Form.List>

          <Form.Item
            label="Mã OTP xác nhận"
            name="otpConfirm"
            rules={rules.otpConfirm}
          >
            <Input placeholder="Nhập OTP xác nhận" style={INPUT_STYLE} />
          </Form.Item>

          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Cập nhật
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
}
